/**
 * Endereço do app autenticado.
 *
 * Até aqui, trocar de módulo era só estado React: a URL ficava em "/" o tempo
 * todo. Três coisas quebravam por causa disso, e todas doem mais no celular:
 *
 *   1. O botão voltar do sistema (Android, e o gesto de borda no iOS) não tinha
 *      para onde voltar. Num PWA instalado ele FECHA o app em vez de sair da
 *      tela, que é o jeito mais rápido de perder um usuário.
 *   2. Não existia alvo para link profundo: clique em notificação, atalho do
 *      manifest e "abrir no app" não têm como apontar para uma tela.
 *   3. Recarregar caía sempre no primeiro projeto da lista, porque o projeto
 *      corrente nunca foi persistido em lugar nenhum.
 *
 * Formato:
 *   /p/<uuid>/<módulo>   módulos que vivem dentro de um projeto
 *   /<módulo>            módulos que atravessam projetos (Minhas Tarefas, Home...)
 *
 * Os ids de módulo NÃO são traduzidos, pelo mesmo motivo que `MODULE_FEATURE_MAP`
 * (src/utils/featureConfig.ts) não traduz: eles são identificadores do gate de
 * plano, e o roteamento reusa exatamente os mesmos. Traduzir quebraria os dois.
 *
 * O app autenticado não carrega prefixo de idioma na URL, decisão já registrada
 * em src/i18n/locale.ts: o idioma vem da preferência persistida, e só a parte
 * pública (landing, blog, legal) usa /en e /es no endereço.
 */

/** Todo módulo que o switch de `renderModule` (src/App.tsx) sabe renderizar. */
export const APP_MODULES = [
  'hoje',
  'home',
  'portfolio',
  'settings',
  'gantt',
  'kanban',
  'team',
  'schedule',
  'calendar',
  'todos',
  'raci',
  'eap',
  'risks',
  'changes',
  'costs',
  'wiki',
  'mytasks',
] as const;

export type AppModule = (typeof APP_MODULES)[number];

/** Mesmo default do `renderModule`, que cai no Gantt quando não reconhece. */
export const DEFAULT_MODULE: AppModule = 'gantt';

/**
 * Módulos que não pertencem a um projeto. Colocar um id de projeto na URL deles
 * seria mentira: o Portfólio lê a carteira inteira e Minhas Tarefas atravessa
 * todos os projetos do usuário.
 */
const PROJECTLESS_MODULES: readonly AppModule[] = [
  'hoje',
  'home',
  'portfolio',
  'settings',
  'wiki',
  'mytasks',
];

export interface AppRoute {
  projectId: string | null;
  module: AppModule | null;
}

const UUID = '[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}';
const PROJECT_ROUTE = new RegExp(`^/p/(${UUID})/([a-z]+)/?$`);
const MODULE_ROUTE = /^\/([a-z]+)\/?$/;

export function isAppModule(value: unknown): value is AppModule {
  return typeof value === 'string' && (APP_MODULES as readonly string[]).includes(value);
}

export function moduleNeedsProject(module: AppModule): boolean {
  return !PROJECTLESS_MODULES.includes(module);
}

/**
 * Lê o endereço. Devolve nulo no que não reconhece, em vez de chutar: quem
 * chama decide o fallback (a URL "/" da raiz é o caso normal de primeiro
 * acesso, e ela não deve virar navegação no histórico).
 */
export function parseAppPath(pathname: string): AppRoute {
  const projectMatch = pathname.match(PROJECT_ROUTE);
  if (projectMatch) {
    const module = projectMatch[2];
    if (isAppModule(module)) {
      return { projectId: projectMatch[1], module };
    }
    return { projectId: projectMatch[1], module: null };
  }

  const moduleMatch = pathname.match(MODULE_ROUTE);
  if (moduleMatch && isAppModule(moduleMatch[1])) {
    return { projectId: null, module: moduleMatch[1] };
  }

  return { projectId: null, module: null };
}

/**
 * Monta o endereço. Sem projeto selecionado (conta nova, antes do primeiro
 * projeto) o módulo vai sozinho no caminho: é mais honesto do que inventar um
 * id, e o parse devolve o mesmo módulo de volta.
 */
export function buildAppPath(route: { projectId: string | null; module: string }): string {
  const module = isAppModule(route.module) ? route.module : DEFAULT_MODULE;

  if (!moduleNeedsProject(module) || !route.projectId) {
    return `/${module}`;
  }

  return `/p/${route.projectId}/${module}`;
}

/** O caminho atual já é um endereço de app? Decide push contra replace. */
export function isAppPath(pathname: string): boolean {
  return parseAppPath(pathname).module !== null;
}

/**
 * Tarefa aberta dentro do módulo, em query e não em caminho.
 *
 * O caminho responde "que TELA é esta"; a tarefa aberta é foco dentro da tela,
 * não outra tela. Em query, ela some sozinha quando o usuário troca de módulo
 * e o `useAppRouteSync` continua preservando a string inteira sem saber que
 * este parâmetro existe.
 */
export const TASK_PARAM = 'task';

/** Lê a tarefa pedida pelo endereço atual. Nulo quando não há nenhuma. */
export function readTaskParam(search: string = window.location.search): string | null {
  return new URLSearchParams(search).get(TASK_PARAM);
}

/**
 * Link compartilhável de uma tarefa.
 *
 * É um endereço INTERNO: ele aponta, não concede. Quem abrir precisa estar
 * logado e ser do projeto, porque quem decide o acesso continua sendo o RLS.
 * Compartilhamento com quem não tem conta é outro caminho, o `/share/<token>`
 * do projeto (PublishModal).
 */
export function buildTaskLink(projectId: string, taskId: string): string {
  const path = buildAppPath({ projectId, module: 'kanban' });
  return `${window.location.origin}${path}?${TASK_PARAM}=${taskId}`;
}

/**
 * O que o celular entrega. Vive aqui, e nao no App.tsx, porque duas coisas
 * distantes precisam da mesma verdade: o `renderModule` (para nao montar tela
 * de planejamento no telefone) e o `UIStateContext` (para nao ABRIR o app num
 * modulo salvo que nao funciona ali).
 */
export const MODULOS_DE_CELULAR = new Set<string>([
  'hoje', 'kanban', 'mytasks', 'home', 'settings', 'wiki',
]);
